import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/lib/AuthContext";

import LoginPage from "@/pages/admin/Login";
import ForgotPasswordPage from "@/pages/admin/ForgotPassword";
import ResetPasswordPage from "@/pages/admin/ResetPassword";
import AppShell from "@/components/admin/AppShell";
import RequireAuth from "@/components/admin/RequireAuth";
import Dashboard from "@/pages/admin/Dashboard";
import SitesList, { prefetchSitesList } from "@/pages/admin/SitesList";
import SiteWizard from "@/pages/admin/SiteWizard";
import DomainsOnboard from "@/pages/admin/DomainsOnboard";
import SiteDetail from "@/pages/admin/SiteDetail";
import Moderation from "@/pages/admin/Moderation";
import AutoReview from "@/pages/admin/AutoReview";
import Inbox from "@/pages/admin/Inbox";
import Postmaster from "@/pages/admin/Postmaster";
import SettingsPage from "@/pages/admin/Settings";
import TemplateGallery from "@/pages/admin/TemplateGallery";
import DeleteArticle from "@/pages/admin/DeleteArticle";
import Capacity from "@/pages/admin/Capacity";

import PublicSiteHome from "@/pages/public/PublicSiteHome";
import PublicArticle from "@/pages/public/PublicArticle";
import PublicContact from "@/pages/public/PublicContact";
import PublicUnsubscribe from "@/pages/public/PublicUnsubscribe";
import PublicLegal from "@/pages/public/PublicLegal";

// Direct load / refresh of the Sites page: start its data requests now, in
// parallel with the session check, instead of after the auth gate resolves.
if (typeof window !== "undefined" && window.location.pathname === "/admin/sites") prefetchSitesList();

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
        <AuthProvider>
        <Routes>
          {/* Root → login form (this is an internal admin tool) */}
          <Route path="/" element={<Navigate to="/admin/login" replace />} />

          {/* Admin */}
          <Route path="/admin/login" element={<LoginPage />} />
          <Route path="/admin/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/admin/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/admin"
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="sites" element={<SitesList />} />
            <Route path="sites/new" element={<SiteWizard />} />
            <Route path="domains" element={<DomainsOnboard />} />
            <Route path="sites/:id" element={<SiteDetail />} />
            <Route path="moderation" element={<Moderation />} />
            <Route path="auto-review" element={<AutoReview />} />
            <Route path="inbox" element={<Inbox />} />
            <Route path="postmaster" element={<Postmaster />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="template-gallery" element={<TemplateGallery />} />
            <Route path="delete-article" element={<DeleteArticle />} />
            <Route path="capacity" element={<Capacity />} />
          </Route>

          {/* Public site preview (Slice 1: rendered as React routes;
              Slice 3: Cloudflare Workers SSR per custom domain) */}
          <Route path="/site/:slug" element={<PublicSiteHome />} />
          <Route path="/site/:slug/category/:category" element={<PublicSiteHome />} />
          <Route path="/site/:slug/article/:articleSlug" element={<PublicArticle />} />
          <Route path="/site/:slug/contact" element={<PublicContact />} />
          <Route path="/site/:slug/unsubscribe" element={<PublicUnsubscribe />} />
          <Route path="/site/:slug/privacy" element={<PublicLegal pageKey="privacy" />} />
          <Route path="/site/:slug/terms" element={<PublicLegal pageKey="terms" />} />
          <Route path="/site/:slug/ccpa" element={<PublicLegal pageKey="ccpa" />} />
          <Route path="/site/:slug/about" element={<PublicLegal pageKey="about" />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </AuthProvider>
      </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
